import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Grid,
  TextField,
  InputAdornment,
  Button,
  makeStyles,
  MenuItem,
  ListSubheader,
} from "@material-ui/core";
import { BrowserView, MobileView, isMobile } from "react-device-detect";
import FlexView from "react-flexview/lib";
import { Controller, useForm } from "react-hook-form";
import { SessionContextStore } from "../../../context/SessionContext";
import { OptionalTag } from "../../Core/OptionalTag";
import {
  EventEmitterService,
  EventKey,
} from "../../../services/EventEmitterService";
import "../UserProfile.scss";
import {
  ACTIONS,
  UserProfileContextStore,
} from "../../../context/UserProfileContext";
import { Action } from "../../../models/Action";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DataLayerService from "../../../services/DataLayerService";
import generalApi from "../../../api/general.api";
import SearchIcon from "@material-ui/icons/Search";
import { UserApi } from "../../../api/user.api";
import { ReactComponent as AddSvg } from "../../../assets/add.svg";
import DeleteIcon from "@material-ui/icons/Delete";
import { awaitWithLoading } from "../../../services/utils";

const hidden_field = "hiddenField";
const membershipCourse_field = "membershipCourse";
const membershipCourseValue_field = "membershipCourseValue";

const useStylesButton = makeStyles((theme) => ({
  root: {
    backgroundColor: "#c7cbcf",
    "&:hover": {
      backgroundColor: "#b1b6bb",
    },
  },
}));

export const MembershipCourseSubSection = ({
  membershipCoursesList,
  membershipsCourse,
  idx,
  agree,
}) => {
  const context = useContext(UserProfileContextStore);
  const classes = useStylesButton();
  const {
    register,
    watch,
    triggerValidation,
    setValue,
    errors,
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
  });

  const [membershipFilter, setMembershipFilter] = useState("");
  const hiddenButton = useRef(null);

  useEffect(() => {
    reset({
      agree: agree,
      [hidden_field]: membershipsCourse,
      [membershipCourse_field]: membershipsCourse?.code,
      [membershipCourseValue_field]:
        membershipsCourse?.type === "Course"
          ? membershipsCourse.file_name
          : membershipsCourse?.value,
    });
  }, [membershipsCourse]);

  useEffect(() => {
    setValue("agree", agree);
    const reValidate = async () => await triggerValidation();
    reValidate();
  }, [agree]);

  useEffect(() => {
    const value = watch(membershipCourse_field);
    if (!value) return;

    const currentValue = watch(hidden_field);
    const selected =
      value === "none"
        ? {}
        : {
            ...membershipCoursesList.filter((x) => x.code === value)[0],
          };

    if (selected.code !== currentValue.code) {
      reset({
        [hidden_field]: selected,
        [membershipCourse_field]: value,
        [membershipCourseValue_field]: null,
        agree: agree,
      });
    }
    setMembershipFilter("");
  }, [watch(membershipCourse_field)]);

  useEffect(() => {
    const current = watch(membershipCourseValue_field);
    const selected = watch(hidden_field);

    if (selected && selected.type === "Membership") {
      selected.value = current;
    }
  }, [watch(membershipCourseValue_field)]);

  useEffect(() => {
    let id = EventEmitterService.subscribe(
      EventKey.USER_PROFILE_SAVE,
      async () => {
        let valid = await triggerValidation();
        const values = getValues();

        context.dispatch(
          new Action(ACTIONS.SAVE_MEMBERSHIP_COURSE, {
            value: values[hidden_field],
            valid:
              (valid && values.agree) ||
              (!values.agree && !values[membershipCourseValue_field]),
            idx,
          })
        );
      }
    );

    return function cleanup() {
      EventEmitterService.unsubscribe(EventKey.USER_PROFILE_SAVE, id);
    };
  }, []);

  const onfileSelected = async (event) => {
    if (event.target.files.length === 0) return;
    const selected = watch(hidden_field);
    const file = event.target.files[0];

    var res = await awaitWithLoading(
      UserApi.uploadCertificate(selected.name, selected.code, file)
    );
    selected.value = res.data.id;
    selected.fileName = file.name;
    setValue(membershipCourseValue_field, file.name);
    const reValidate = async () => await triggerValidation();
    reValidate();
    DataLayerService.pushToDataLayer({ event: "course-uploaded" });
  };

  return (
    <React.Fragment>
      <Grid container justify="center" spacing={2}>
        <Grid item xs={12} md={6}>
          <input
            type="file"
            accept="application/pdf,image/*"
            id="file"
            onChange={(e) => onfileSelected(e)}
            ref={hiddenButton}
            style={{ display: "none" }}
          />

          <Controller
            name={hidden_field}
            control={control}
            defaultValue=""
            as={<input type="hidden" />}
          />

          <Controller
            name={"agree"}
            control={control}
            value={agree}
            as={<input value={agree} type="hidden" />}
          />

          <FlexView className="margin-top-basic">
            {
              <Controller
                control={control}
                rules={{
                  validate: (value) =>
                    value.trim() === "none" ||
                    watch("agree") ||
                    "Must agree to the checkbox",
                }}
                name={membershipCourse_field}
                defaultValue={"none"}
                as={
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    className={isMobile ? "" : "margin-right-basic"}
                    label="Select Course / Membership"
                    error={!!errors[membershipCourse_field]}
                    helperText={
                      !!errors[membershipCourse_field]
                        ? errors[membershipCourse_field].message
                        : ""
                    }
                  >
                    <MenuItem key="" value="none">
                      <em>Select Membership/Course</em>
                    </MenuItem>
                    {
                      <ListSubheader>
                        <TextField
                          size="small"
                          autoFocus
                          placeholder="Type to filter..."
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                          onChange={(e) =>
                            setMembershipFilter(e.target.value.toUpperCase())
                          }
                          onKeyDown={(e) => {
                            if (e.key !== "Escape") {
                              e.stopPropagation();
                            }
                          }}
                        />
                      </ListSubheader>
                    }
                    {membershipCoursesList
                      .filter(
                        (x) =>
                          x.name.toUpperCase().indexOf(membershipFilter) !== -1
                      )
                      .map((o) => (
                        <MenuItem key={o.name} value={o.code}>
                          {o.name}
                        </MenuItem>
                      ))}
                  </TextField>
                }
              />
            }
          </FlexView>
        </Grid>
        <Grid item xs={12} md={6}>
          <FlexView className="margin-top-basic">
            {watch(hidden_field) && watch(hidden_field).type === "Course" && (
              <TextField
                disabled
                onClick={() => hiddenButton.current.click()}
                name={membershipCourseValue_field}
                fullWidth
                variant="outlined"
                label="Upload Certification/Invoice Proof"
                InputLabelProps={{
                  shrink: !!watch(membershipCourseValue_field),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      (
                      <Button
                        className={classes.root}
                        variant="contained"
                        color="primary"
                        aria-label="upload-company-logo"
                      >
                        <CloudUploadIcon className="white" />
                      </Button>
                      )
                    </InputAdornment>
                  ),
                }}
                inputRef={register({
                  required: "Required",
                  validate: (value) => value.trim() !== "" || "Required",
                })}
                error={!!errors[membershipCourseValue_field]}
                helperText={
                  errors[membershipCourseValue_field]
                    ? errors[membershipCourseValue_field].message
                    : ""
                }
                value={watch(hidden_field)?.filename}
              />
            )}{" "}
            {watch(hidden_field) &&
              watch(hidden_field).type === "Membership" && (
                <TextField
                  InputLabelProps={{
                    shrink: !!watch(membershipCourseValue_field),
                  }}
                  fullWidth
                  variant="outlined"
                  label="Membership id"
                  name={membershipCourseValue_field}
                  // defaultValue={watch(membershipCourseValue_field) || ""}
                  value={watch(membershipCourseValue_field) || ""}
                  inputRef={register({
                    required: "Required",
                    validate: (value) => value.trim() !== "" || "Required",
                  })}
                  helperText={
                    errors[membershipCourseValue_field]
                      ? errors[membershipCourseValue_field].message
                      : ""
                  }
                  error={!!errors[membershipCourseValue_field]}
                />
              )}
          </FlexView>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
